import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import datasetConfig from '../dataset.json';

const ModelsScreen = () => {
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);

  // Clear response when model changes
  useEffect(() => {
    setResponse(null);
    setError(null);
    setSelectedFile(null);
    setFileInputKey(prev => prev + 1);
  }, [selectedModel]);

  const getFileType = (model) => {
    if (!model) {
      return '*/*'; // Allow all file types when no model is selected
    }
    if (model.startsWith('xray_dataset') || model === 'skin_dataset') {
      return 'image/*';
    } else if (model === 'cough_dataset') {
      return 'audio/wav';
    }
    return '*/*';
  };

  const getEndpoint = (model) => {
    if (model === 'skin_dataset') {
      return '/symptoms/analyze-image';
    } else if (model === 'cough_dataset') {
      return '/cough/analyze';
    } else if (model.startsWith('xray_dataset/')) {
      return '/symptom-checker/x-ray-scan';
    }
    return null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Enhanced validation with specific error messages
    if (!selectedModel) {
      setError('❌ Please select a model/dataset to analyze your file');
      return;
    }
    
    if (!selectedFile) {
      setError('❌ Please upload a file to analyze');
      return;
    }

    // Validate file type based on selected model
    const expectedFileType = getFileType(selectedModel);
    if (expectedFileType !== '*/*') {
      const isImageModel = selectedModel.startsWith('xray_dataset') || selectedModel === 'skin_dataset';
      const isAudioModel = selectedModel === 'cough_dataset';
      
      if (isImageModel && !selectedFile.type.startsWith('image/')) {
        setError(`❌ Invalid file type. This model requires an image file (PNG, JPG, JPEG). You uploaded: ${selectedFile.type}`);
        return;
      }
      
      if (isAudioModel && !selectedFile.type.startsWith('audio/')) {
        setError(`❌ Invalid file type. This model requires an audio file (WAV). You uploaded: ${selectedFile.type}`);
        return;
      }
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (selectedFile.size > maxSize) {
      setError(`❌ File too large. Maximum size is 10MB. Your file is ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setResponse(null);

    try {
      const endpoint = getEndpoint(selectedModel);
      if (!endpoint) {
        throw new Error('Invalid model selected');
      }

      const formData = new FormData();
      
      if (selectedModel.startsWith('xray_dataset/')) {
        // For X-ray models, add type and image
        const type = selectedModel.split('/')[1]; // chest, hand, leg, spine
        formData.append('type', type);
        formData.append('image', selectedFile);
      } else if (selectedModel === 'skin_dataset') {
        // For skin dataset, upload as image
        formData.append('image', selectedFile);
      } else if (selectedModel === 'cough_dataset') {
        // For cough dataset, upload as audio
        formData.append('audio', selectedFile);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const result = await axios.post(endpoint, formData, config);
      setResponse(result.data);
    } catch (err) {
      console.error('Error analyzing file:', err);
      
      // Enhanced error handling with specific messages
      let errorMessage = '❌ Failed to analyze file';
      
      if (err.code === 'ERR_NETWORK') {
        errorMessage = '❌ Network Error: Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.response?.status === 404) {
        errorMessage = '❌ Service Not Found: The analysis service is currently unavailable. Please try again later.';
      } else if (err.response?.status === 413) {
        errorMessage = '❌ File Too Large: The uploaded file exceeds the maximum allowed size. Please use a smaller file.';
      } else if (err.response?.status === 415) {
        errorMessage = '❌ Unsupported File Type: The file format is not supported by this model. Please check the file type requirements.';
      } else if (err.response?.status === 422) {
        errorMessage = '❌ Invalid File: The uploaded file could not be processed. Please ensure the file is not corrupted and try again.';
      } else if (err.response?.status === 500) {
        errorMessage = '❌ Server Error: The analysis service encountered an internal error. Please try again later.';
      } else if (err.response?.status === 503) {
        errorMessage = '❌ Service Unavailable: The analysis service is temporarily down. Please try again in a few minutes.';
      } else if (err.response?.data?.message) {
        errorMessage = `❌ ${err.response.data.message}`;
      } else if (err.message) {
        errorMessage = `❌ ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const isSubmitDisabled = !selectedModel || !selectedFile || isAnalyzing;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">AI Model Testing</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Model Selection */}
            <div>
              <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 mb-2">
                🔽 Select Dataset/Model
              </label>
              <select
                id="model-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Choose a model...</option>
                {datasetConfig.dataset.map((model) => (
                  <option key={model} value={model}>
                    {model.replace('_', ' ').replace('/', ' - ')}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div>
              <label htmlFor="file-upload" className="block text-sm font-medium text-gray-700 mb-2">
                📤 Upload File
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        key={fileInputKey}
                        name="file-upload"
                        type="file"
                        accept={getFileType(selectedModel)}
                        onChange={handleFileChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    {!selectedModel 
                      ? 'Select a model to see supported file types'
                      : selectedModel.startsWith('xray_dataset') || selectedModel === 'skin_dataset'
                        ? 'PNG, JPG, JPEG up to 10MB'
                        : 'WAV files up to 10MB'
                    }
                  </p>
                </div>
              </div>
              {selectedFile && (
                <div className="mt-2 text-sm text-gray-600">
                  📎 Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>

            {/* Submit Button */}
            {selectedModel && selectedFile && (
              <div>
                <button
                  type="submit"
                  disabled={isSubmitDisabled}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isSubmitDisabled
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  }`}
                >
                  {isAnalyzing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analyzing...
                    </>
                  ) : (
                    '🧠 Analyze with AI'
                  )}
                </button>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">Analysis Failed</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="whitespace-pre-wrap">{error}</p>
                      {error.includes('Network Error') && (
                        <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                          <strong>💡 Troubleshooting tips:</strong>
                          <ul className="mt-1 ml-4 list-disc">
                            <li>Check your internet connection</li>
                            <li>Ensure the backend server is running</li>
                            <li>Try refreshing the page</li>
                          </ul>
                        </div>
                      )}
                      {error.includes('File Too Large') && (
                        <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                          <strong>💡 Solution:</strong> Compress your file or use a smaller one (max 10MB)
                        </div>
                      )}
                      {error.includes('Invalid file type') && (
                        <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                          <strong>💡 Supported formats:</strong>
                          {selectedModel?.startsWith('xray_dataset') || selectedModel === 'skin_dataset' 
                            ? ' PNG, JPG, JPEG files'
                            : selectedModel === 'cough_dataset'
                            ? ' WAV audio files'
                            : ' Check model requirements'
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Response Display */}
            {response && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-green-800">Analysis Complete</h3>
                    <div className="mt-2">
                      <pre className="text-sm text-green-700 bg-white p-3 rounded border overflow-x-auto">
                        {JSON.stringify(response, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModelsScreen; 